import React, { FunctionComponent } from 'react'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'

import { WhiteGreyBtn } from 'Stories/Button/Button.stories'
import defaultCover from 'Shared/img/defaultCover.png'
import { Mobile, Desktop } from 'Shared/components/Responsive'
import { capitalizeFirstLetter } from 'Helpers/layoutHelpers'
import { colors } from 'Styled/variables'
import { playlistRequirementsLink } from 'Constants/links'

import * as S from './styles'
import * as P from './props'

const PlayListCard: FunctionComponent<P.PlayListCardProps> = ({
  playListName,
  followers,
  onGenresEdit,
  playlistID,
  selectedGenres,
  meetConditions,
  playlistCover,
  score,
  stats,
  extraScore,
  //  eslint-disable-next-line @typescript-eslint/no-explicit-any
  status,
}) => {
  const { t } = useTranslation()

  const calculateListeners = (
    stats: Array<{ listeners: number; streams: number }>
  ) => {
    if (!stats || stats.length < 1) return 'no data'
    const averageListeners =
      stats.reduce((a, b) => a + b.listeners, 0) / stats.length
    return `${averageListeners.toFixed()}`
  }

  const calculateStreams = (
    stats: Array<{ listeners: number; streams: number }>
  ) => {
    if (!stats || stats.length < 1) return 'no data'
    const averageStreams =
      stats.reduce((a, b) => a + b.streams, 0) / stats.length
    return `${averageStreams.toFixed()}`
  }

  const setStatus = () => {
    if (status === 'deactivated') {
      return (
        <S.Status color={colors.messegesRed}>
          {t('playlistsPage.inactive')}
        </S.Status>
      )
    }
    if (isEmpty(selectedGenres) || !meetConditions) {
      return <S.Status color={colors.white} />
    }
    if (meetConditions && status === 'activated') {
      return (
        <S.Status color={colors.green}>{t('playlistsPage.active')}</S.Status>
      )
    }
    if (meetConditions && status === 'on_review') {
      return (
        <S.Status color={colors.orange}>{t('playlistsPage.onReview')}</S.Status>
      )
    }
    return (
      <S.Status color={colors.messegesRed}>
        {t('playlistsPage.inactive')}
      </S.Status>
    )
  }

  function renderGenres() {
    if (!meetConditions) {
      return (
        <S.Status color={colors.messegesRed} leftAlign>
          {t('playlistsPage.doesNotMeetConditions')}
        </S.Status>
      )
    }
    if (isEmpty(selectedGenres)) {
      return (
        <S.Status color={colors.messegesRed} leftAlign>
          {t('playlistsPage.emptyGenres')}
        </S.Status>
      )
    }
    return (
      <S.Genres>
        {selectedGenres.map(
          (item, key) =>
            // @ts-ignore
            `${capitalizeFirstLetter(item.Genre.genre_name)}${
              key < selectedGenres.length - 1 ? `, ` : ``
            }`
        )}
      </S.Genres>
    )
  }

  return (
    <>
      <Desktop>
        <S.TableRow>
          <S.TableData width="56px">
            <S.PlaylistCover
              alt="playlist-cover"
              src={playlistCover || defaultCover}
            />
          </S.TableData>
          <S.TableData fill>
            <S.MainInfo>
              <S.Text>{playListName}</S.Text>
              <S.Text>
                <span>{t('playlistsPage.playlistScore')}: </span>
                {score + extraScore}
              </S.Text>
            </S.MainInfo>
          </S.TableData>
          <S.TableData width="152px">
            <S.PlaylistStats>
              <S.Text>
                <span>{t('playlistsPage.followers')}: </span>
                {followers}
              </S.Text>
              <S.Text>
                <span>{t('playlistsPage.listeners')}: </span>
                {stats && calculateListeners(stats)}
              </S.Text>
              <S.Text>
                <span>{t('playlistsPage.streams')}: </span>
                {stats && calculateStreams(stats)}
              </S.Text>
            </S.PlaylistStats>
          </S.TableData>
          <S.TableData fill>{renderGenres()}</S.TableData>
          <S.TableData width="104px">{setStatus()}</S.TableData>
          <S.TableData width="120px" center>
            {meetConditions ? (
              <WhiteGreyBtn
                color="white"
                onClick={() => onGenresEdit(playlistID, false)}
              >
                {t('playlistsPage.edit')}
              </WhiteGreyBtn>
            ) : (
              <S.ContactSupportBtn
                href={playlistRequirementsLink}
                target="_blank"
              >
                {t('playlistsPage.learnMore')}
              </S.ContactSupportBtn>
            )}
          </S.TableData>
        </S.TableRow>
      </Desktop>
      <Mobile>
        <S.MobileCard>
          <S.PlaylistCover
            alt="playlist-cover"
            src={playlistCover || defaultCover}
          />
          <S.MobileCardRightContent>
            <S.TableDataMobile>
              <S.Text>{playListName}</S.Text>
              <S.Text>
                <span>{t('playlistsPage.playlistScore')}: </span>
                {score + extraScore}
              </S.Text>
            </S.TableDataMobile>
            <S.TableDataMobile>
              <S.PlaylistStats>
                <S.Text>
                  <span>{t('playlistsPage.followers')}: </span>
                  {followers}
                </S.Text>
                <S.Text>
                  <span>{t('playlistsPage.listeners')}: </span>
                  {stats && calculateListeners(stats)}
                </S.Text>
                <S.Text>
                  <span>{t('playlistsPage.streams')}: </span>
                  {stats && calculateStreams(stats)}
                </S.Text>
              </S.PlaylistStats>
            </S.TableDataMobile>
            <S.TableDataMobile>{renderGenres()}</S.TableDataMobile>
            {!isEmpty(selectedGenres) && meetConditions && (
              <S.TableDataMobile>{setStatus()}</S.TableDataMobile>
            )}
            <S.TableDataMobile>
              {meetConditions ? (
                <WhiteGreyBtn
                  color="white"
                  onClick={() => onGenresEdit(playlistID, false)}
                >
                  {t('playlistsPage.edit')}
                </WhiteGreyBtn>
              ) : (
                <S.ContactSupportBtn
                  href={playlistRequirementsLink}
                  target="_blank"
                >
                  {t('playlistsPage.contactSupport')}
                </S.ContactSupportBtn>
              )}
            </S.TableDataMobile>
          </S.MobileCardRightContent>
        </S.MobileCard>
      </Mobile>
    </>
  )
}

export default PlayListCard
